/**
 * `GET /api/dashboard-data` as a DEVICE sees it (#582 S4) → the columns the live board draws.
 *
 * The server's device projection (`dashboard.service.js` `getDeviceDashboardData`) is
 * `{ users: [{ id, firstName, avatarUrl, chores: [...] }], unassignedChores, meta: { kind: 'device' } }`
 * — every member, safe fields only, no rewards. This keeps it that way: nothing here adds money,
 * a role or a username, and a missing name stays missing rather than becoming a sample name.
 *
 * `completed: true` on a chore means "finished in the last ninety seconds", not "finished today"
 * (the query's own window, recorded in `lib/dashboardMapping.js`). So a completed chore is drawn
 * lit, and NO "X of Y lit today" count is derived — the payload cannot support one.
 */

function text(value) {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/**
 * @returns {Array<{id:number, name:string, avatar:string|null, quests:Array<{id:number, title:string, status:'unlit'|'lit', ownerId:number}>}>}
 */
export function toBoardKids(board) {
  const users = Array.isArray(board?.users) ? board.users : [];
  return users
    .filter((user) => user && typeof user === 'object' && user.id != null)
    .map((user) => ({
      id: user.id,
      // `Display` renders a nameless column as "Kid"; say "Family member" instead, which is true.
      name: text(user.firstName) ?? 'Family member',
      avatar: text(user.avatarUrl),
      quests: (Array.isArray(user.chores) ? user.chores : [])
        .filter((chore) => chore && typeof chore === 'object' && chore.id != null)
        .map((chore) => ({
          id: chore.id,
          title: text(chore.name) ?? 'Quest',
          status: chore.completed === true ? 'lit' : 'unlit',
          ownerId: user.id,
        })),
    }));
}

/**
 * #583 d-20261001-41 — the household's unassigned chores, for the board's "Anyone can do these" row.
 *
 * The server already sends them (`getDeviceDashboardData`, `unassignedChores`, reward stripped, and
 * a chore completed in the board's window left out). `ownerId: null` is what says "nobody's yet".
 *
 * @returns {Array<{id:number, title:string, status:'unlit', ownerId:null}>}
 */
export function toSharedQuests(board) {
  const chores = Array.isArray(board?.unassignedChores) ? board.unassignedChores : [];
  return chores
    .filter((chore) => chore && typeof chore === 'object' && chore.id != null)
    .map((chore) => ({ id: chore.id, title: text(chore.name) ?? 'Quest', status: 'unlit', ownerId: null }));
}

/**
 * #582 (line 63) — completed chores explicitly reported as no longer awaiting approval.
 *
 * The server marks each completed chore with `awaitingApproval` (a bare boolean). Only an explicit
 * `false` counts; a missing flag (an older server) supplies no fact. This also includes completions
 * that never needed approval: callers may infer a review ONLY for a receipt previously pending.
 * Keyed by completion id, never chore id, so a sibling's approval or an
 * older completion of the same quest cannot move another receipt.
 *
 * @returns {Set<string>} completion ids, as strings
 */
export function toReviewedCompletions(board) {
  const users = Array.isArray(board?.users) ? board.users : [];
  return new Set(users.flatMap((user) => (Array.isArray(user?.chores) ? user.chores : []))
    .filter((chore) => chore?.completed === true && chore.completionId != null && chore.awaitingApproval === false)
    .map((chore) => String(chore.completionId)));
}

/**
 * #582 (line 63) — the completions the server says were undone in the board's ninety-second window.
 *
 * The server names them (`undoneCompletionIds`, ids only). A completion that is merely missing from
 * a read proves nothing — that read may have started before it committed — so only this list does.
 *
 * @returns {Set<string>} completion ids, as strings
 */
export function toUndoneCompletions(board) {
  const ids = Array.isArray(board?.undoneCompletionIds) ? board.undoneCompletionIds : [];
  return new Set(ids.filter((id) => id != null).map(String));
}

/**
 * #577 (d-20260924-03) — the board's `meta.sound` → the feedback preferences a display plays by.
 *
 * The server already ANDed each sound with the family's kiosk permission
 * (`dashboard.service.js` `loadDisplaySound`) and sends only the two a display can play. So
 * `kioskSound` here is true exactly when one of them is, which keeps the provider's own kiosk gate
 * meaningful rather than bypassed. Anything missing or not a real `true` is silence. The payday
 * fanfare and the haptic are never on for a display.
 *
 * @returns {{completionChime:boolean, paydayFanfare:false, villageAmbience:boolean, kioskSound:boolean, softHaptic:false}}
 */
export function toKioskSound(board) {
  const sound = board?.meta?.sound;
  const completionChime = sound?.completionChime === true;
  const villageAmbience = sound?.villageAmbience === true;
  return {
    completionChime,
    paydayFanfare: false,
    villageAmbience,
    kioskSound: completionChime || villageAmbience,
    softHaptic: false,
  };
}
